import { ExecutionPlan } from './chunk-HBQMUHJW.js';
import { SubCard } from './chunk-W2MDQRQA.js';
import { GENERIC_COMPONENTS } from './chunk-RIC2ISCZ.js';
import { ProtocolIcon } from './chunk-Q3P6T5DK.js';
import { ProtocolBadge } from './chunk-OVXROGOV.js';
import { SectionCard, ProtocolValue, isRecord, humanizeFieldName, FactGrid, fieldAtPath, pickField, ProtocolParamList, ProtocolDisclosure, decodedOrRaw, legField } from './chunk-IFYJS34L.js';
import { Workflow, Activity, Braces } from 'lucide-react';
import * as React from 'react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { RawToggle } from '@authe/ui-core';

function SummaryBody({
  params,
  summaryFields,
  drilldownLabel,
  emptyLabel
}) {
  const facts = React.useMemo(
    () => summaryFields.map((name) => {
      const value = name.includes(".") ? fieldAtPath(params, name) : pickField(params, name);
      return { label: humanizeFieldName(name.split(".").at(-1)), value: /* @__PURE__ */ jsx(ProtocolValue, { value }), when: value != null };
    }),
    [params, summaryFields]
  );
  if (!facts.some((fact) => fact.when)) {
    return /* @__PURE__ */ jsx(ProtocolParamList, { params, emptyLabel });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    /* @__PURE__ */ jsx(ProtocolDisclosure, { className: "mt-3 min-w-0", summary: drilldownLabel, children: /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params, emptyLabel }) }) })
  ] });
}
function GenericCallCard({ call, hint, children }) {
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: hint.protocolId, icon: Workflow }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint.label ?? call.label ?? "Call" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: hint.protocolId,
      meta: hint.protocolId ? /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: hint.protocolId }) : void 0,
      children: [
        children,
        children ? /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs", children: "All call arguments" }),
          /* @__PURE__ */ jsx(SummaryBody, { params: call.params, summaryFields: [], drilldownLabel: "All call arguments", emptyLabel: "No decoded arguments" })
        ] }) : /* @__PURE__ */ jsx(
          SummaryBody,
          {
            params: call.params,
            summaryFields: hint.summaryFields ?? [],
            drilldownLabel: "All call arguments",
            emptyLabel: "No decoded arguments"
          }
        )
      ]
    }
  );
}
function GenericEventCard({ event, hint }) {
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: hint.protocolId, icon: Activity }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint.label ?? event.name ?? "Event" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: hint.protocolId,
      meta: hint.protocolId ? /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: hint.protocolId }) : void 0,
      children: [
        event.address && /* @__PURE__ */ jsxs("div", { className: "mb-2 flex min-w-0 flex-wrap items-center gap-1 text-xs", children: [
          /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "Emitted by" }),
          /* @__PURE__ */ jsx(ProtocolValue, { value: event.address, type: "address" })
        ] }),
        /* @__PURE__ */ jsx(
          SummaryBody,
          {
            params: event.params,
            summaryFields: hint.summaryFields ?? [],
            drilldownLabel: "All event fields",
            emptyLabel: "No decoded fields"
          }
        )
      ]
    }
  );
}
function GenericStructValue({
  decoded,
  display,
  raw,
  hint,
  chain,
  confidence
}) {
  const struct = isRecord(decoded) ? decoded : void 0;
  const facts = React.useMemo(() => {
    if (!struct) return [];
    const named = Array.isArray(hint?.fields) ? hint.fields.filter((name) => typeof name === "string") : [];
    const ordered = [...new Set(named)].filter((name) => Object.hasOwn(struct, name));
    const rest = Object.keys(struct).filter((name) => !named.includes(name));
    return [...ordered, ...rest].filter((name) => struct[name] != null && struct[name] !== "").map((name) => {
      const value = struct[name];
      const content = /* @__PURE__ */ jsx(ProtocolValue, { value, chain });
      const detailed = isRecord(value) && !("@decoded" in value) && Object.keys(value).length > 4;
      return {
        label: humanizeFieldName(name),
        value: detailed ? /* @__PURE__ */ jsxs("details", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground", children: "View details" }),
          content
        ] }) : content,
        when: value != null && value !== ""
      };
    });
  }, [struct, hint?.fields, chain]);
  if (facts.length === 0) {
    return /* @__PURE__ */ jsxs("span", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw, chain }),
      /* @__PURE__ */ jsx(RawToggle, { raw })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    SubCard,
    {
      caption: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx(Braces, { className: "size-3 shrink-0", "aria-hidden": true }),
        hint?.label ?? "Decoded",
        typeof confidence === "number" && Number.isFinite(confidence) && confidence >= 0 && confidence <= 1 && /* @__PURE__ */ jsxs("span", { className: "font-normal normal-case", title: "Confidence in interpreting this value; not proof of execution or settlement", children: [
          "\xB7 Interpretation ",
          Math.round(confidence * 100),
          "%"
        ] })
      ] }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsx(RawToggle, { raw })
      ]
    }
  );
}
function EvmCallBatchCard(props) {
  const value = decodedOrRaw(fieldAtPath(props.call.params, ...props.hint.summaryFields ?? []));
  if (!Array.isArray(value)) return /* @__PURE__ */ jsx(GenericCallCard, { ...props, hint: { ...props.hint, summaryFields: [] } });
  return /* @__PURE__ */ jsx(GenericCallCard, { ...props, hint: { ...props.hint, summaryFields: [] }, children: /* @__PURE__ */ jsx(ExecutionPlan, { items: value, label: () => "Call", renderItem: (item) => {
    const target = legField(item, "to");
    const amount = legField(item, "value");
    const data = legField(item, "data");
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(FactGrid, { facts: [
        { label: "Target", value: /* @__PURE__ */ jsx(ProtocolValue, { value: target }), when: target != null },
        { label: "Native value (base units)", value: /* @__PURE__ */ jsx(ProtocolValue, { value: amount }), when: amount != null }
      ] }),
      /* @__PURE__ */ jsx(ProtocolValue, { value: data ?? item })
    ] });
  } }) });
}

// src/protocols/_generic/index.ts
function registerGeneric(registry) {
  registry.registerEvent(GENERIC_COMPONENTS.event, GenericEventCard);
  registry.registerCall(GENERIC_COMPONENTS.call, GenericCallCard);
  registry.registerCall(GENERIC_COMPONENTS.callBatch, EvmCallBatchCard);
  registry.registerValue(GENERIC_COMPONENTS.struct, GenericStructValue);
  return registry;
}

export { GenericCallCard, GenericEventCard, GenericStructValue, registerGeneric };
//# sourceMappingURL=chunk-2H3ENTSI.js.map
//# sourceMappingURL=chunk-2H3ENTSI.js.map